import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type GroupDefaults = components['schemas']['CompanyGroupDefaultResource']
export type GroupDefaultsUpdateInput = components['schemas']['UpdateGroupDefaultsRequest']
export type TemplateDepartment = components['schemas']['TemplateDepartmentResource']
export type TemplatePosition = components['schemas']['TemplatePositionResource']

/** The generated request schema describes localized values as an array. */
export type TemplateItemInput = { name: Record<string, string>; code: string }

export function useGroupDefaults(groupId: string | null) {
  return useQuery({
    queryKey: ['platform', 'groups', groupId, 'defaults'],
    enabled: groupId !== null,
    queryFn: async (): Promise<GroupDefaults> => {
      const result = await api.GET('/platform/groups/{group}/defaults', {
        params: { path: { group: groupId ?? '' } },
      })
      if (!result.data) throw new Error('GET /platform/groups/{group}/defaults returned no body')
      // The generated response omits the nullable branding field on this
      // operation, while the resource schema includes it.
      return result.data.data as GroupDefaults
    },
  })
}

export function useUpdateGroupDefaults() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, input }: { groupId: string; input: GroupDefaultsUpdateInput }) =>
      api.PATCH('/platform/groups/{group}/defaults', {
        params: { path: { group: groupId } },
        body: input,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'defaults'],
      })
    },
  })
}

export function useTemplateDepartments(groupId: string | null) {
  return useQuery({
    queryKey: ['platform', 'groups', groupId, 'template-departments'],
    enabled: groupId !== null,
    queryFn: async (): Promise<TemplateDepartment[]> => {
      const result = await api.GET('/platform/groups/{group}/template-departments', {
        params: { path: { group: groupId ?? '' } },
      })
      if (!result.data)
        throw new Error('GET /platform/groups/{group}/template-departments returned no body')
      return result.data.data
    },
  })
}

export function useCreateTemplateDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, input }: { groupId: string; input: TemplateItemInput }) =>
      api.POST('/platform/groups/{group}/template-departments', {
        params: { path: { group: groupId } },
        body: input as unknown as components['schemas']['TemplateItemStoreRequest'],
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'template-departments'],
      })
    },
  })
}

export function useUpdateTemplateDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      groupId,
      templateDepartmentId,
      input,
    }: {
      groupId: string
      templateDepartmentId: string
      input: TemplateItemInput
    }) =>
      api.PATCH('/platform/groups/{group}/template-departments/{templateDepartment}', {
        params: { path: { group: groupId, templateDepartment: templateDepartmentId } },
        body: input as unknown as components['schemas']['TemplateItemUpdateRequest'],
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'template-departments'],
      })
    },
  })
}

export function useDeleteTemplateDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      groupId,
      templateDepartmentId,
    }: {
      groupId: string
      templateDepartmentId: string
    }) =>
      api.DELETE('/platform/groups/{group}/template-departments/{templateDepartment}', {
        params: { path: { group: groupId, templateDepartment: templateDepartmentId } },
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'template-departments'],
      })
    },
  })
}

export function useTemplatePositions(groupId: string | null) {
  return useQuery({
    queryKey: ['platform', 'groups', groupId, 'template-positions'],
    enabled: groupId !== null,
    queryFn: async (): Promise<TemplatePosition[]> => {
      const result = await api.GET('/platform/groups/{group}/template-positions', {
        params: { path: { group: groupId ?? '' } },
      })
      if (!result.data)
        throw new Error('GET /platform/groups/{group}/template-positions returned no body')
      return result.data.data
    },
  })
}

export function useCreateTemplatePosition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, input }: { groupId: string; input: TemplateItemInput }) =>
      api.POST('/platform/groups/{group}/template-positions', {
        params: { path: { group: groupId } },
        body: input as unknown as components['schemas']['TemplateItemStoreRequest'],
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'template-positions'],
      })
    },
  })
}

export function useUpdateTemplatePosition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      groupId,
      templatePositionId,
      input,
    }: {
      groupId: string
      templatePositionId: string
      input: TemplateItemInput
    }) =>
      api.PATCH('/platform/groups/{group}/template-positions/{templatePosition}', {
        params: { path: { group: groupId, templatePosition: templatePositionId } },
        body: input as unknown as components['schemas']['TemplateItemUpdateRequest'],
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'template-positions'],
      })
    },
  })
}

export function useDeleteTemplatePosition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      groupId,
      templatePositionId,
    }: {
      groupId: string
      templatePositionId: string
    }) =>
      api.DELETE('/platform/groups/{group}/template-positions/{templatePosition}', {
        params: { path: { group: groupId, templatePosition: templatePositionId } },
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'template-positions'],
      })
    },
  })
}
